import { useMutation } from "@tanstack/react-query";
import { Loader2, Send, Trash2 } from "lucide-react";
import React, { useState } from "react";

import { addComment, deleteComment, type CommentView, type ItemView } from "@/api/okk_receiving";
import { useAuth } from "@/auth/useAuth";
import { Button } from "@/components/ui/button";
import { fmtDateTime } from "@/lib/format";
import { authorName } from "@/lib/okk-receiving";
import { apiErrorText } from "@/lib/scan-ui/screen";

/** Лента комментариев: автор, время, текст, привязка к позиции. Удалить — своё
 *  (админ — любое; сервер всё равно проверит и ответит 403 с текстом). */
export function CommentList({
  inspectionId,
  comments,
  items,
  onChanged,
}: {
  inspectionId: string;
  comments: CommentView[];
  /** Для подписи «к позиции …»; не передан — привязку не показываем. */
  items?: ItemView[];
  onChanged: () => void;
}): React.ReactElement {
  const { user } = useAuth();
  const [error, setError] = useState("");
  const remove = useMutation({
    mutationFn: (id: string) => deleteComment(inspectionId, id),
    onMutate: () => setError(""),
    onSuccess: () => onChanged(),
    onError: (e) => setError(`Комментарий не удалён: ${apiErrorText(e)}`),
  });
  const itemName = (id: string | null) =>
    id ? (items?.find((i) => i.id === id)?.nomenclature_name ?? "позиция") : null;

  if (comments.length === 0 && !error) {
    return <p className="text-xs text-muted-foreground">Комментариев нет</p>;
  }
  return (
    <div className="space-y-2">
      {comments.map((c) => {
        const mine = Boolean(user && (user.is_admin || c.author?.id === user.id));
        const about = items ? itemName(c.item_id) : null;
        return (
          <div key={c.id} data-testid="okk-comment" className="rounded-md border bg-muted/30 p-2 text-sm">
            <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{authorName(c.author)}</span>
              <span>{fmtDateTime(c.created_at)}</span>
              {about && <span className="rounded bg-muted px-1">к позиции: {about}</span>}
              {mine && (
                <button
                  type="button"
                  aria-label="Удалить комментарий"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm("Удалить комментарий?")) remove.mutate(c.id);
                  }}
                  className="ml-auto text-muted-foreground hover:text-rose-700"
                >
                  <Trash2 className="size-3.5" />
                </button>
              )}
            </div>
            <p className="mt-1 whitespace-pre-wrap break-words">{c.text}</p>
          </div>
        );
      })}
      {error && (
        <div role="alert" className="text-sm text-rose-700">
          {error}
        </div>
      )}
    </div>
  );
}

/** Новый комментарий — к проверке (`itemId=null`) или к позиции. Комментарии
 *  можно и к завершённой проверке: это лента, а не решение. */
export function CommentForm({
  inspectionId,
  itemId,
  items,
  onAdded,
  placeholder = "Комментарий",
}: {
  inspectionId: string;
  /** Фиксированная привязка (карточка позиции). `undefined` — выбор в форме. */
  itemId?: string | null;
  /** Позиции для выбора привязки (только когда `itemId` не задан). */
  items?: ItemView[];
  onAdded: () => void;
  placeholder?: string;
}): React.ReactElement {
  const [text, setText] = useState("");
  const [target, setTarget] = useState("");
  const [error, setError] = useState("");
  const fixed = itemId !== undefined;
  const send = useMutation({
    mutationFn: () =>
      addComment(inspectionId, { text: text.trim(), item_id: fixed ? itemId : target || null }),
    onMutate: () => setError(""),
    onSuccess: () => {
      setText("");
      onAdded();
    },
    // Текст остаётся в поле — переписывать заново не придётся.
    onError: (e) => setError(apiErrorText(e)),
  });
  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (text.trim()) send.mutate();
      }}
    >
      {!fixed && items && items.length > 0 && (
        <select
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          aria-label="К чему комментарий"
          className="h-9 w-full rounded-md border bg-background px-2 text-sm"
        >
          <option value="">К проверке целиком</option>
          {items
            .filter((i) => !i.removed_from_delivery)
            .map((i) => (
              <option key={i.id} value={i.id}>
                К позиции: {i.nomenclature_name}
              </option>
            ))}
        </select>
      )}
      <div className="flex gap-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
          rows={2}
          className="min-h-[2.5rem] flex-1 rounded-md border bg-background px-2 py-1.5 text-sm"
        />
        <Button type="submit" size="icon" disabled={!text.trim() || send.isPending} aria-label="Отправить комментарий">
          {send.isPending ? <Loader2 className="animate-spin" /> : <Send />}
        </Button>
      </div>
      {error && (
        <div role="alert" className="text-sm text-rose-700">
          {error}
        </div>
      )}
    </form>
  );
}
